import styled, { css } from 'styled-components';

import { ChatWriteIcon, SaveIcon } from '../../../../assets';
import ShareIcon2 from '../../../../assets/ShareIcon2';
import { SCROLLABLE_CARD_MIN_WIDTH } from '../../../../components/VehicleCard/VehicleBoxesAndActions/VehicleBoxAndActions.styles';
import ZIndexLayers from '../../../../services/ZIndexLayers';
import { ThemeProp } from '../../../../theme/Themes';
import { setSVGChildColors } from '../../../../utils/Listings';
import { DESKTOP_RIGHT_PADDING, ListingsIconButton } from '../Listing.styles';

export const defaultColumnWidth = 5.5;

export const VehicleCardContainer = styled.div`
  margin-bottom: 1rem;
  z-index: ${ZIndexLayers.elevateOne};
  cursor: pointer;
  min-width: 1080px;
  position: relative;
`;

export const Header = styled.div`
  display: flex;
  gap: 2.25rem;
  margin-bottom: 0.5rem;
  min-width: ${SCROLLABLE_CARD_MIN_WIDTH};
  padding-left: 1.25rem;
  padding-bottom: 0 !important;
  top: 0;
  background: transparent;
  z-index: ${ZIndexLayers.fixedElement};
`;

interface HeaderTitleProps {
  $customWidth?: number;
}

export const HeaderTitle = styled.div<HeaderTitleProps>`
  width: ${(props: HeaderTitleProps) => props.$customWidth ?? defaultColumnWidth}rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

export const HeaderTitleContainer = styled.div`
  margin-left: 1rem;
  width: 14.2rem;
  margin-bottom: 0.5rem;
  position: sticky;
  top: 0;
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  z-index: ${ZIndexLayers.fixedElement};
  p {
    margin-left: -1rem;
  }
`;

export const ListingCardsMobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const ShimmersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    padding: 0 1rem;
  }
`;

export const ShimmerHeaderTitleContainer = styled.div`
  width: 15.2rem;
`;

export const ShimmerDesktopHeaderContainer = styled.div`
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export const IconButton = styled(ListingsIconButton)`
  background: transparent;
  box-shadow: none;
`;

export const StyledChatWriteIcon = styled(ChatWriteIcon)`
  width: 1.125rem;
  height: 1.125rem;
`;

export const ShareIconButton = styled(IconButton)`
  width: 2rem;
  height: 2rem;
  background: ${(props: ThemeProp) => props.theme.gradients.button.default};

  svg {
    path {
      stroke: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
    }
  }

  &:hover {
    background: ${(props: ThemeProp) => props.theme.gradients.button.default};
    svg {
      path {
        stroke: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
      }
    }
  }
`;

export const VEHICLE_INFO_COLUMN_WIDTH = '17.5rem';

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  width: ${VEHICLE_INFO_COLUMN_WIDTH};
  min-width: ${VEHICLE_INFO_COLUMN_WIDTH};
`;

export const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: ${SCROLLABLE_CARD_MIN_WIDTH};
`;

export const ScrollableColumn = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 0.25rem;

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    padding-right: ${DESKTOP_RIGHT_PADDING};
  }

  ::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }
`;

export const ShimmerScrollableColumn = styled.div`
  display: flex;
`;

export const ScrollDiv = styled.div`
  height: 0.865rem;
  display: none;
  width: calc(100% - ${VEHICLE_INFO_COLUMN_WIDTH});
  overflow-x: scroll;
  overflow-y: hidden;
  margin-left: ${VEHICLE_INFO_COLUMN_WIDTH};

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }

  ::-webkit-scrollbar {
    height: 0.5rem;
    width: 0.375rem;
    border-radius: 1rem;
    background-image: initial;
    background-position-x: initial;
    background-position-y: initial;
    background-size: initial;
    background-repeat-x: initial;
    background-repeat-y: initial;
    background-attachment: initial;
    background-origin: initial;
    background-clip: initial;
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  }

  ::-webkit-scrollbar-thumb {
    background-image: initial;
    background-position-x: initial;
    background-position-y: initial;
    background-size: initial;
    background-repeat-x: initial;
    background-repeat-y: initial;
    background-attachment: initial;
    background-origin: initial;
    background-clip: initial;
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
    border-radius: 0.5rem;
  }
`;

export interface HeaderIconButtonProps {
  $focused?: boolean;
}

export const StyledIconButton = styled(IconButton)<HeaderIconButtonProps>`
  margin: 0;
  padding: 0;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  border-radius: 50%;
  ${(props: ThemeProp) => setSVGChildColors(props.theme.colors.neutrals.c400)}

  &:hover {
    background: ${(props) => props.theme.colors.neutrals.c100};
    ${(props: ThemeProp) => setSVGChildColors(props.theme.colors.neutrals.c400)}
  }
`;

export const StyledSaveIcon = styled(SaveIcon)<{ $isSelected?: boolean }>`
  width: 0.95rem;
  height: 0.95rem;

  ${(props) =>
    props.$isSelected &&
    css`
      path {
        fill: ${(props: ThemeProp) => props.theme.colors.main.c600};
        stroke: ${(props: ThemeProp) => props.theme.colors.main.c600} !important;
      }
    `}
`;

export const StyledShareIcon = styled(ShareIcon2)`
  width: 1.25rem;
  height: 1.25rem;
`;

export const LeftSideContainer = styled.div`
  z-index: ${ZIndexLayers.elevateOne};
  cursor: pointer;
  position: relative;

  &:last-child {
    padding-bottom: 0.5rem;
  }
`;

export const RightSideContainer = styled.div`
  z-index: ${ZIndexLayers.elevateOne};
  cursor: pointer;
  position: relative;
  flex: 1;
`;

export const TableContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
  margin-bottom: 0.5rem;
  overflow-y: visible;
  position: relative;
  /* By default, hide checkboxes */
  .checkbox-container {
    visibility: hidden;
  }

  /* Show checkbox on hover when no selections */
  &:not(.has-selected-listings) {
    /* Left side hover */
    ${Column} ${LeftSideContainer}:hover .checkbox-container {
      visibility: visible;
    }

    /* Right side hover using CSS variable */
    ${Column} ${LeftSideContainer}[data-hovered="true"] .checkbox-container {
      visibility: visible;
    }
  }

  /* Always show checkboxes when there are selections */
  &.has-selected-listings {
    .checkbox-container {
      visibility: visible;
    }
  }
`;
